import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { ViewState } from '../types';

interface NavbarProps {
  currentView: ViewState;
  setView: (view: ViewState) => void;
}

export function Navbar({ currentView, setView }: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleNav = (view: ViewState) => {
    setView(view);
    setIsOpen(false);
  };

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-[60] bg-mier text-stone-50 text-[9px] sm:text-[10px] text-center py-1.5 tracking-widest uppercase font-light">
        Designed and developed by <a href="https://skycresce.com/" target="_blank" rel="noopener noreferrer" className="hover:text-stone-300 transition-colors font-medium">skycresce.com</a>
      </div>
      
      <header className="fixed top-6 sm:top-7 left-0 right-0 z-50 px-6 py-4 flex items-center justify-between mix-blend-difference text-stone-50">
        <button onClick={() => setView('home')} className="font-serif text-2xl tracking-widest uppercase" aria-label="MIER Home">
          MIER.
        </button>
        
        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8 text-sm uppercase tracking-widest font-medium">
          <button onClick={() => { setView('home'); window.scrollTo(0,0); }} className={`hover:opacity-70 transition-opacity ${currentView === 'home' ? 'opacity-100' : 'opacity-50'}`}>Home</button>
          <button 
            onClick={() => {
              setView('about');
              window.scrollTo(0, 0);
            }} 
            className={`hover:opacity-70 transition-opacity ${currentView === 'about' ? 'opacity-100' : 'opacity-50'}`}
          >
            About
          </button>
          <button 
            onClick={() => {
              setView('services');
              window.scrollTo(0, 0);
            }} 
            className={`hover:opacity-70 transition-opacity ${currentView === 'services' ? 'opacity-100' : 'opacity-50'}`}
          >
            Services
          </button>
          <button 
            onClick={() => {
              setView('gallery');
              window.scrollTo(0, 0);
            }} 
            className={`hover:opacity-70 transition-opacity ${currentView === 'gallery' ? 'opacity-100' : 'opacity-50'}`}
          >
            Gallery
          </button>
          <button 
            onClick={() => {
              setView('blog');
              window.scrollTo(0, 0);
            }} 
            className={`hover:opacity-70 transition-opacity ${currentView === 'blog' ? 'opacity-100' : 'opacity-50'}`}
          >
            Blog
          </button>
          <button onClick={() => { setView('booking'); window.scrollTo(0,0); }} className={`hover:opacity-70 transition-opacity ${currentView === 'booking' ? 'opacity-100' : 'opacity-50'}`}>Book Online</button>
        </nav>

        {/* Mobile Toggle */}
        <button 
          className="md:hidden p-2 -mr-2 text-stone-50"
          onClick={() => setIsOpen(true)}
          aria-label="Open mobile menu"
        >
          <Menu className="w-6 h-6" />
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-50 bg-mier text-stone-50 p-6 flex flex-col"
          >
            <div className="flex justify-between items-center py-2 mt-6">
              <span className="font-serif text-2xl tracking-widest uppercase">MIER.</span>
              <button onClick={() => setIsOpen(false)} className="p-2 -mr-2" aria-label="Close mobile menu">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <nav className="flex flex-col gap-8 mt-24 text-2xl font-serif">
              <button 
                onClick={() => { setView('home'); window.scrollTo(0,0); setIsOpen(false); }}
                className={`text-left ${currentView === 'home' ? 'text-stone-50' : 'text-stone-400'}`}
              >
                Home
              </button>
              <button 
                onClick={() => {
                  setView('about');
                  window.scrollTo(0, 0);
                  setIsOpen(false);
                }}
                className={`text-left ${currentView === 'about' ? 'text-stone-50' : 'text-stone-400'}`}
              >
                About
              </button>
              <button 
                onClick={() => {
                  setView('services');
                  window.scrollTo(0, 0);
                  setIsOpen(false);
                }}
                className={`text-left ${currentView === 'services' ? 'text-stone-50' : 'text-stone-400'}`}
              >
                Services
              </button>
              <button 
                onClick={() => {
                  setView('gallery');
                  window.scrollTo(0, 0);
                  setIsOpen(false);
                }}
                className={`text-left ${currentView === 'gallery' ? 'text-stone-50' : 'text-stone-400'}`}
              >
                Gallery
              </button>
              <button 
                onClick={() => {
                  setView('blog');
                  window.scrollTo(0, 0);
                  setIsOpen(false);
                }}
                className={`text-left ${currentView === 'blog' ? 'text-stone-50' : 'text-stone-400'}`}
              >
                Blog
              </button>
              <button 
                onClick={() => { setView('booking'); window.scrollTo(0,0); setIsOpen(false); }}
                className={`text-left ${currentView === 'booking' ? 'text-stone-50' : 'text-stone-400'}`}
              >
                Book Online
              </button>
            </nav>
            
            <div className="mt-auto pb-12">
              <p className="text-xs uppercase tracking-widest text-stone-400 mb-2">Location</p>
              <p className="font-serif text-lg">Aloft Hotel, Al Rawdah<br />Abu Dhabi, UAE</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
